<template>
  <div class="flex justify-start items-baseline">
    <OnClickOutside class="relative mr-8" @trigger="isMonthOptionsDropdownOpen = false">
      <SlotButton
        id="month-options-dropdown-trigger"
        class="flex items-center justify-between gap-x-4 w-40 h-10"
        @click="isMonthOptionsDropdownOpen = !isMonthOptionsDropdownOpen"
        alt
      >
        <span>{{ currentMonth.label }}</span>
        <ChevronDown class="w-4 h-4" />
      </SlotButton>
      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="opacity-0 translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-2"
      >
        <div
          id="month-options-dropdown"
          class="absolute left-0 mt-2 bg-surface-primary shadow-podHover rounded-md w-52 max-h-80 overflow-y-auto z-10 flex flex-col py-2"
          v-show="isMonthOptionsDropdownOpen"
        >
          <span
            v-for="month in monthOptions"
            :key="month.value"
            :id="`month-option-${month.value}`"
            class="transition-all px-4 py-2 hover:bg-surface-base cursor-pointer"
            :class="month.value === currentMonth.value ? 'bg-surface-base font-semibold' : ''"
            @click="monthOptionClick(month)"
          >
            {{ month.label }}
          </span>
        </div>
      </transition>
    </OnClickOutside>
    <span
      v-for="activityType in activityTypes"
      :key="activityType"
      :id="`activity-type-${activityType}`"
      class="cursor-pointer mr-4 capitalize"
      :class="
        activityType === currentActivityType
          ? 'font-semibold text-fg-primary'
          : 'text-fg-link-primary'
      "
      @click="activityTypeChange(activityType)"
    >
      {{ activityType }}
    </span>
  </div>
</template>

<script lang="ts">
import { OnClickOutside } from '@vueuse/components'
import moment from 'moment'
import { PropType, defineComponent } from 'vue'

import ChevronDown from '@/assets/chevronDown.svg?component'
import SlotButton from '@/common/components/atoms/SlotButton.vue'

interface MonthOption {
  value: string
  label: string
}
type ActivityType = 'cash' | 'accrual'

export default defineComponent({
  name: 'AccountingSummaryFilter',
  components: { SlotButton, ChevronDown, OnClickOutside },
  props: {
    month: {
      type: String,
      required: true,
    },
    activityType: {
      type: String as PropType<ActivityType>,
      required: true,
    },
  },
  emits: {
    // eslint-disable-next-line no-unused-vars
    'update:month': (month: string) => true,
    // eslint-disable-next-line no-unused-vars
    'update:activityType': (activityType: ActivityType) => true,
  },
  data() {
    return {
      isMonthOptionsDropdownOpen: false,
      activityTypes: ['cash', 'accrual'] as ActivityType[],
      monthOptions: Array.from({ length: 24 }, (_, i) => {
        const month = moment().subtract(i, 'months')
        return {
          value: month.format('YYYY-MM'),
          label: month.format('MMMM YYYY'),
        }
      }) as MonthOption[],
    }
  },
  methods: {
    monthOptionClick(month: MonthOption): void {
      this.isMonthOptionsDropdownOpen = false
      this.$emit('update:month', month.value)
    },
    activityTypeChange(activityType: ActivityType): void {
      this.$emit('update:activityType', activityType)
    },
  },
  computed: {
    currentMonth(): MonthOption {
      return this.monthOptions.find(month => month.value === this.month) as MonthOption
    },
    currentActivityType(): ActivityType {
      return this.activityType as ActivityType
    },
  },
})
</script>
